import React, { useLayoutEffect, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useLangNavigate } from 'shared/hooks';

import { AUTO_SLIDE_INTERVAL, EVENT_BLOG_SLUG, EVENT_IMAGES } from '../constants';
import { useAutoSlide } from '../hook/useAutoSlide';
import { usePopupVisibility } from '../hook/usePopupVisibility';

const EventPopup = () => {
  const { t } = useTranslation();
  const { lang } = useLangNavigate();
  const { isVisible, hideForToday, hideForSession } = usePopupVisibility();
  const { currentIndex, goToNext, goToPrev } = useAutoSlide({
    totalSlides: EVENT_IMAGES.length,
    interval: AUTO_SLIDE_INTERVAL,
    isActive: isVisible,
  });
  const [exitingIndex, setExitingIndex] = useState<number | null>(null);
  const prevIndexRef = useRef(currentIndex);

  // 카드 전환: 직전 카드를 왼쪽으로 밀어내는 동안만 새 카드와 겹쳐서 렌더한다.
  // useLayoutEffect 인 이유: paint 전에 직전 카드를 세워둬야 한 프레임 비는 깜빡임이 없다
  useLayoutEffect(() => {
    if (prevIndexRef.current !== currentIndex) {
      setExitingIndex(prevIndexRef.current);
      prevIndexRef.current = currentIndex;
    }
  }, [currentIndex]);

  if (!isVisible) return null;

  const eventBlogHref = lang ? `/${lang}/blog/${EVENT_BLOG_SLUG}/` : `/blog/${EVENT_BLOG_SLUG}/`;

  // GTM 트리거가 mall 팝업 시절 이름으로 걸려 있다. 팝업 내용이 이벤트로 바뀌어도
  // 이 값을 바꾸면 클릭 집계가 끊기므로 그대로 유지한다.
  const handleCtaClick = () => {
    window.dataLayer?.push({ event: 'click_main', menu: 'explore', section: 'Checkmate Mall Popup' });
  };

  return (
    <div data-nosnippet className="pointer-events-none fixed bottom-0 right-4 z-50 hidden md:right-[90px] md:block">
      <div className="pointer-events-auto relative flex w-[90vw] max-w-[420px] animate-slideUpFromBottom flex-col items-center gap-[32px] overflow-hidden rounded-t-[20px] bg-gradient-to-b from-[#ffe4e4] to-[#fff8f8] pb-0 pt-[28px] shadow-[0px_4px_40px_0px_rgba(0,0,0,0.15)]">
        {/* Content Section */}
        <div className="flex w-full flex-col items-center">
          <div className="flex flex-col items-center gap-[30px]">
            {/* Header */}
            <div className="flex flex-col items-center gap-[16px] px-4">
              {/* User Benefits Badge */}
              <div className="flex items-center justify-center rounded-bl-[8px] rounded-br-[8px] rounded-tl-[2px] rounded-tr-[2px] border-[1.4px] border-solid border-[rgba(255,45,46,0.4)] bg-[#ffe9e9] px-[8px] py-[3px]">
                <p className="text-[14px] font-semibold leading-[1.4] tracking-[-0.3px] text-[#ff2d2e]">
                  {t('common:eventPopup.badge')}
                </p>
              </div>

              {/* Title */}
              <div className="flex w-full flex-col items-center gap-[2px] text-center text-[18px] leading-[1.4] tracking-[-0.4px] text-[#222]">
                <div className="flex w-full flex-col justify-center font-normal">
                  <p>{t('common:eventPopup.title1')}</p>
                </div>
                <div className="flex flex-col justify-center font-bold">
                  <p>{t('common:eventPopup.title2')}</p>
                </div>
              </div>
            </div>

            {/* Event Photo Slider */}
            <div className="flex items-center gap-[16px] px-2">
              {/* Left Arrow */}
              <button
                onClick={goToPrev}
                className="flex h-[18px] w-[18px] shrink-0 items-center justify-center overflow-hidden transition-opacity hover:opacity-70"
                aria-label="Previous photo"
              >
                <svg width="9" height="15" viewBox="0 0 11 18" fill="none" className="rotate-180">
                  <path
                    d="M1.5 1.5L9 9L1.5 16.5"
                    stroke="#3F3F3F"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>

              {/* Event Photo — 카드째 슬라이드되며, 뷰포트 좌우 16px 경계는 mask 그라데이션으로 페이드 */}
              <div className="relative -mx-[16px] h-[220px] w-[232px] shrink-0 overflow-hidden [-webkit-mask-image:linear-gradient(to_right,transparent,black_16px,black_calc(100%_-_16px),transparent)] [mask-image:linear-gradient(to_right,transparent,black_16px,black_calc(100%_-_16px),transparent)]">
                {exitingIndex !== null && (
                  <div
                    key={`exit-${exitingIndex}`}
                    onAnimationEnd={() => setExitingIndex(null)}
                    className="absolute left-[16px] top-0 h-[220px] w-[200px] animate-slideCardOutToLeft overflow-hidden rounded-[10px]"
                  >
                    <img src={EVENT_IMAGES[exitingIndex]} alt="" className="h-full w-full object-cover" />
                  </div>
                )}
                <div
                  key={currentIndex}
                  className="absolute left-[16px] top-0 h-[220px] w-[200px] animate-slideCardInFromRight"
                >
                  <button
                    onClick={goToNext}
                    className="h-full w-full overflow-hidden rounded-[10px] transition-transform hover:scale-[1.02]"
                    aria-label="Next photo"
                  >
                    <img
                      src={EVENT_IMAGES[currentIndex]}
                      alt={`Transit Lounge Yongsan ${currentIndex + 1}`}
                      className="h-full w-full object-cover"
                    />
                  </button>
                </div>
              </div>

              {/* Right Arrow */}
              <button
                onClick={goToNext}
                className="flex h-[18px] w-[18px] shrink-0 items-center justify-center overflow-hidden transition-opacity hover:opacity-70"
                aria-label="Next photo"
              >
                <svg width="9" height="15" viewBox="0 0 11 18" fill="none">
                  <path
                    d="M1.5 1.5L9 9L1.5 16.5"
                    stroke="#3F3F3F"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="flex w-full flex-col items-start">
          {/* CTA Button — 블로그는 별도 앱이라 SPA 라우팅이 아닌 실제 이동이어야 한다 */}
          <a
            href={eventBlogHref}
            onClick={handleCtaClick}
            className="flex w-full items-center bg-gradient-to-r from-[#ff6b6c] to-[#ff2d2e] px-[16px] py-[14px] transition-opacity hover:opacity-90"
          >
            <div className="flex flex-1 flex-col justify-center text-center text-[17px] font-semibold leading-[1.4] tracking-[-0.4px] text-white">
              <p>{t('common:eventPopup.ctaButton')}</p>
            </div>
          </a>

          {/* Footer Actions */}
          <div className="flex w-full items-center justify-between whitespace-nowrap bg-white px-[20px] py-[14px] text-[13px] leading-[1.5] tracking-[-0.3px]">
            <button
              onClick={hideForToday}
              className="flex flex-col justify-center text-[#8e8e8e] transition-opacity hover:opacity-70"
            >
              <p>{t('common:eventPopup.hideToday')}</p>
            </button>
            <button
              onClick={hideForSession}
              className="flex flex-col justify-center text-center text-[#222] transition-opacity hover:opacity-70"
            >
              <p>{t('common:eventPopup.close')}</p>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventPopup;
